import useDeepCompareEffect from 'react-use/lib/useDeepCompareEffect';

import {
  AccessPoint_bool_exp,
  Organization_bool_exp,
  Property_bool_exp,
  Unit_bool_exp,
  usegetGrantableAccessPointsQuery,
  usegetGrantableUnitsQuery,
  usegetOrganizationsQuery,
  usegetPropertiesQuery,
} from '../../../../graphql/hasura/generated';

import { getScopeQueryFilters } from './getScopeQueryFilters';
import { handleScopeQueriesResultChanged } from './handleScopeQueriesResultChanged';
import type { IScopeQueriesResult, IUseScopeQueriesArgs } from './typings';

const fetchPolicy = 'cache-first';

export function useScopeQueries(args: IUseScopeQueriesArgs): IScopeQueriesResult {
  const filters = getScopeQueryFilters(args);
  const { permissionScope, setFieldValue } = args;

  const organizationsResult = usegetOrganizationsQuery({
    fetchPolicy,
    skip: !filters.organizationFilter,
    variables: {
      where: filters.organizationFilter as Organization_bool_exp,
    },
  });

  const propertiesResult = usegetPropertiesQuery({
    fetchPolicy,
    skip: !filters.propertyFilter,
    variables: {
      where: filters.propertyFilter as Property_bool_exp,
    },
  });

  const unitsResult = usegetGrantableUnitsQuery({
    fetchPolicy,
    skip: !filters.unitFilter,
    variables: {
      where: filters.unitFilter as Unit_bool_exp,
    },
  });

  const accessPointsResult = usegetGrantableAccessPointsQuery({
    fetchPolicy,
    skip: !filters.accessPointFilter,
    variables: {
      where: filters.accessPointFilter as AccessPoint_bool_exp,
    },
  });

  const scopeQueriesResult = {
    organizations: organizationsResult.data?.organizations,
    properties: propertiesResult.data?.properties || [],
    units: unitsResult.data?.units || [],
    accessPoints: accessPointsResult.data?.accessPoints || [],
    loading: (
      organizationsResult.loading ||
      propertiesResult.loading ||
      unitsResult.loading ||
      accessPointsResult.loading
    ),
  };

  useDeepCompareEffect(() => {
    handleScopeQueriesResultChanged({ permissionScope, scopeQueriesResult, setFieldValue });
  }, [scopeQueriesResult]);

  return scopeQueriesResult;
}

export type { IScopeQueriesResult };
